import { MazeTemplate, MazeTemplateCategory } from '../../types/maze-types';
import type { GameDifficulty } from '@/contexts/games/types';
import { staticUrl } from '@/lib/static';

const BASE = '/maze-templates';

function template(
  id: string,
  name: string,
  category: MazeTemplateCategory,
  svgFile: string,
  emoji: string,
  /** Base slug for config files (e.g. 'cat' → cat-easy.json, cat-medium.json, etc.) */
  configSlug?: string,
): MazeTemplate {
  return {
    id,
    name,
    category,
    svgPath: `${BASE}/${svgFile}`,
    thumbnail: `${BASE}/${svgFile}`,
    emoji,
    configPath: configSlug ? `${BASE}/${configSlug}` : undefined,
  };
}

export const MAZE_TEMPLATES: MazeTemplate[] = [
  // Animals
  template('tpl-cat', 'Cat', 'animals', 'cat.svg', '\uD83D\uDC31', 'cat'),
  template('tpl-butterfly', 'Butterfly', 'animals', 'butterfly.svg', '\uD83E\uDD8B', 'butterfly'),
  template('tpl-dinosaur', 'Dinosaur', 'animals', 'dinosaur.svg', '\uD83E\uDD95', 'dinosaur'),

  // Transport
  template('tpl-rocket', 'Rocket', 'transport', 'rocket.svg', '\uD83D\uDE80', 'rocket'),
  template('tpl-car', 'Car', 'transport', 'car.svg', '\uD83D\uDE97', 'car'),
  template('tpl-ship', 'Ship', 'transport', 'rocket.svg', '\uD83D\uDEA2', 'ship'),
  template('tpl-airplane', 'Airplane', 'transport', 'airplane.svg', '\u2708\uFE0F', 'airplane'),

  // Nature
  template('tpl-tree', 'Tree', 'nature', 'tree.svg', '\uD83C\uDF33', 'tree'),
  template('tpl-star', 'Star', 'nature', 'flower.svg', '\u2B50', 'star'),
  template('tpl-heart', 'Heart', 'nature', 'flower.svg', '\u2764\uFE0F', 'heart'),
  template('tpl-flower', 'Flower', 'nature', 'flower.svg', '\uD83C\uDF3B', 'flower'),
  template('tpl-mountain', 'Mountain', 'nature', 'mountain.svg', '\u26F0\uFE0F', 'mountain'),

  // Fantasy
  template('tpl-dragon', 'Dragon', 'fantasy', 'dragon.svg', '\uD83D\uDC09', 'dragon'),
  template('tpl-castle', 'Castle', 'fantasy', 'castle.svg', '\uD83C\uDFF0', 'castle'),
  template('tpl-unicorn', 'Unicorn', 'fantasy', 'unicorn.svg', '\uD83E\uDD84', 'unicorn'),
];

/**
 * Get the config path for a template at a specific difficulty.
 * Returns e.g. "/maze-templates/cat-hard.json"
 */
export function getTemplateConfigPath(
  template: MazeTemplate,
  difficulty: GameDifficulty,
): string | undefined {
  if (!template.configPath) return undefined;
  // Fetched by the BROWSER, so it may live on the CDN — unlike the .svg beside
  // it, which the server reads from disk (docs/ops/static-cdn.md).
  return staticUrl(`${template.configPath}-${difficulty}.json`);
}

export function getTemplatesByCategory(
  category: MazeTemplateCategory,
): MazeTemplate[] {
  return MAZE_TEMPLATES.filter((t) => t.category === category);
}

export function getTemplateById(id: string): MazeTemplate | undefined {
  return MAZE_TEMPLATES.find((t) => t.id === id);
}

/** Return only templates that have pre-computed configs (no queue needed). */
export function getTemplatesWithConfig(): MazeTemplate[] {
  return MAZE_TEMPLATES.filter((t) => t.configPath != null);
}

export const TEMPLATE_CATEGORIES: MazeTemplateCategory[] = [
  'animals',
  'transport',
  'nature',
  'fantasy',
];
